import React from 'react';
import { motion } from "framer-motion";
import { Link, useHistory } from "react-router-dom";
import { onDragEnd, getGoal, checkConditions } from "../resources/utils";
import copy from 'copy-to-clipboard';

import styles from './Account.module.scss';

import ProductBannerCard from "../components/ProductBannerCard";
import ProductCard from "../components/ProductCard";
import TierCard from "../components/TierCard";
import Welcomer from "../components/Welcomer";

const variants = {
  enter: {
    x: 1000,
    opacity: 0
  },
  center: {
    zIndex: 1,
    x: 0,
    opacity: 1
  },
  exit: {
    zIndex: 0,
    x: -1000,
    opacity: 0
  }
};

const Account = ({ config, customer, rewards, tiers }) => {
  let history = useHistory();

  // console.log(config);

  let tierRewards = customer ? [ ...customer.tiers ].reduce((result, tierId) => {
    let newResult = [ ...result ];
    let tier = config.tiers.find(x => x.id === tierId);
    if(tier && tier.rewards) {
      for (var i = 0; i < tier.rewards.length; i++) {
        let loopId = tier.rewards[i];
        let reward = rewards.find(x => x.id === loopId);
        let rewardIndex = newResult.findIndex(x => x.id === loopId);
        if(reward && rewardIndex === -1 && reward.discount.__typename !== 'SystemDiscount') {
          if(checkConditions(reward.conditions, customer)) {
            newResult.push(reward);
          }
        }
      }
    }
    return newResult;
  }, []) : [];

  let tiersReversed = [ ...config.tiers ].reverse();
  let highestTier = tiersReversed.find(x => ( tiers.some(z => z.id === x.id) && customer.tiers.includes(x.id))); // Find our highest tier the customer has, checking that it's a valid tier as well.
  let nextTierIndex = tiersReversed.findIndex((o, i) => ((i+1 !== tiersReversed.length) && tiersReversed[i+1] && tiersReversed[i+1].id === highestTier.id) );

  let highestTierData = tiers.find(x => x.id === highestTier.id);
  let nextTierData = (nextTierIndex !== -1) ? tiers.find(x => x.id === tiersReversed[nextTierIndex].id) : null;


  let goalData = nextTierData ? getGoal(highestTierData.conditions, nextTierData.conditions) : null;
  let goalPercentComplete = null;
  let goalField = null;
  let goalProgress = null;
  if(goalData && goalData.field === 'lifetimePoints') {
    goalPercentComplete = (+customer.lifetimePoints - goalData.oldTarget) / (goalData.newTarget - goalData.oldTarget) * 100;
    goalField = 'Points';
    goalProgress = +customer.lifetimePoints;
  }
  if(goalData && goalData.field === 'points') {
    goalPercentComplete = (+customer.points - goalData.oldTarget) / (goalData.newTarget - goalData.oldTarget) * 100;
    goalField = 'Points';
    goalProgress = +customer.points;
  }
  if(goalData && goalData.field === 'lifetimeSpend') {
    goalPercentComplete = (+customer.lifetimeSpend - goalData.oldTarget) / (goalData.newTarget - goalData.oldTarget) * 100;
    goalField = 'Spend';
    goalProgress = +customer.lifetimeSpend;
  }
  if(goalData && goalData.field === 'lifetimeOrders') {
    goalPercentComplete = (+customer.lifetimeOrders - goalData.oldTarget) / (goalData.newTarget - goalData.oldTarget) * 100;
    goalField = 'Orders';
    goalProgress = +customer.lifetimeOrders;
  }
  return (
    <motion.div
    className={styles.account}
    variants={variants}
    initial="enter"
    animate="center"
    exit="exit"
    transition={{
      x: { type: "spring", stiffness: 300, damping: 30 },
      opacity: { duration: 0.2 }
    }}
    drag="x"
    dragConstraints={{ left: 0, right: 0 }}
    dragElastic={0.5}
    onDragEnd={(e, { offset, velocity }) => {
      onDragEnd(
        offset,
        velocity,
        () => {
          history.goBack();
        },
        () => {
          // do nothing
        },
      )
    }}
    >
      <Welcomer logo={config.logo} customer={customer} />
      <div className={styles.tiers}>
        {
          highestTier &&
          <TierCard id={highestTier.id} accent={highestTier.accent} perks={highestTier.perks} image={highestTier.image} />
        }
      </div>
      {
        (goalPercentComplete !== null && goalField !== null) &&
        <div className={styles.nextTier}>
          <div className={styles.ntHeader}>
            <div>To upgrade to <span style={{ color: tiersReversed[nextTierIndex].accent ? `#${tiersReversed[nextTierIndex].accent}` : undefined }}>{ tiersReversed[nextTierIndex].name }</span></div>
            <Link to="/tiers" className={styles.ntLink}>
              <span>Tier Benefits</span>
              <svg width="13" height="7" viewBox="0 0 13 7" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M9.02542 0.09612L8.82024 0.28944C8.68426 0.417573 8.68426 0.62533 8.82024 0.75349L11.2556 3.03516H0.348214C0.155913 3.03516 0 3.18208 0 3.36328V3.63672C0 3.81793 0.155913 3.96485 0.348214 3.96485H11.2556L8.82024 6.24652C8.68426 6.37465 8.68426 6.58241 8.82024 6.71057L9.02542 6.90389C9.1614 7.03202 9.38188 7.03202 9.51786 6.90389L12.898 3.73201C13.034 3.60388 13.034 3.39612 12.898 3.26796L9.51786 0.09612C9.38188 -0.03204 9.1614 -0.03204 9.02542 0.09612Z" fill="#1D1D1E"/>
              </svg>
            </Link>
          </div>
          <div className={styles.ntBar}>
            <div style={{ width: `${goalPercentComplete.toFixed(2)}%`, backgroundColor: tiersReversed[nextTierIndex].accent ? `#${tiersReversed[nextTierIndex].accent}` : undefined }}>
            </div>
          </div>
          <div className={styles.ntFooter}>
            <div>
              <span>{ goalProgress }</span>
              <span>{`${goalField} earned`}</span>
            </div>
            <div>
              <span>{ goalData.newTarget }</span>
              <span>{`${goalField} needed`}</span>
            </div>
          </div>
        </div>
      }
      <div className={styles.rewards}>
        <strong>Featured rewards</strong>
        {
          config.accountFeatured.map(x => {
            let rewardCard = config.rewards.find(z => z.id === x.id);
            let rewardObj = rewards.find(z => z.id === x.id);
            if(rewardCard && rewardObj) {
              if(x.banner) {
                return <ProductBannerCard key={x.id} image={x.bannerImage} title={rewardCard.title} points={rewardObj.points} id={x.id} buttonText={x.bannerButtonText} buttonColour={x.bannerButtonColour} buttonTextColour={x.bannerButtonTextColour} textColour={x.bannerTextColour }/>
              }
              return <ProductCard key={x.id} image={rewardCard.image} title={rewardCard.title} points={rewardObj.points} id={x.id} />
            } else {
              return <div key={x.id}></div>
            }
          })
        }
        <Link to="/rewards" className={styles.button} style={{ backgroundColor: config.buttonColour ? config.buttonColour : undefined}}>All rewards</Link>
      </div>

      {/*
        Tier Rewards section
      */}

      {
        (tierRewards && tierRewards.length !== 0) &&
        <div className={styles.rewards}>
          <strong>Tier rewards</strong>
          {
            tierRewards.map(x => {
              let rewardCard = config.rewards.find(z => z.id === x.id);
              let rewardObj = x;
              if(rewardCard && rewardObj) {
                if(x.banner) {
                  return <ProductBannerCard key={x.id} image={x.bannerImage} title={rewardCard.title} points={rewardObj.points} id={x.id} buttonText={x.bannerButtonText} buttonColour={x.bannerButtonColour} buttonTextColour={x.bannerButtonTextColour} textColour={x.bannerTextColour }/>
                }
                return <ProductCard key={x.id} image={rewardCard.image} title={rewardCard.title} points={rewardObj.points} id={x.id} />
              } else {
                return <div key={x.id}></div>
              }
            })
          }
        </div>
      }

      {/*
        Referral section
      */}
      {
        (customer && customer.referral) &&
        <div className={styles.refer}>
          <strong>Refer your friends</strong>
          <p>Give a friend $5 off their first purchase and earn <strong>50 points</strong> with this code:</p>
          <motion.div whileTap={{ scale: 0.9 }} className={styles.copy} onClick={() => {
            copy(customer.referral);
          }}>
            <span>{ customer.referral }</span>
            <svg width="15" height="15" viewBox="0 0 15 15" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M13.5938 0H4.21875C3.44209 0 2.8125 0.62959 2.8125 1.40625V2.8125H1.40625C0.62959 2.8125 0 3.44209 0 4.21875V13.5938C0 14.3704 0.62959 15 1.40625 15H10.7812C11.5579 15 12.1875 14.3704 12.1875 13.5938V12.1875H13.5938C14.3704 12.1875 15 11.5579 15 10.7812V1.40625C15 0.62959 14.3704 0 13.5938 0ZM10.6055 13.5938H1.58203C1.53541 13.5938 1.4907 13.5752 1.45774 13.5423C1.42477 13.5093 1.40625 13.4646 1.40625 13.418V4.39453C1.40625 4.34791 1.42477 4.3032 1.45774 4.27024C1.4907 4.23727 1.53541 4.21875 1.58203 4.21875H2.8125V10.7812C2.8125 11.5579 3.44209 12.1875 4.21875 12.1875H10.7812V13.418C10.7812 13.4646 10.7627 13.5093 10.7298 13.5423C10.6968 13.5752 10.6521 13.5938 10.6055 13.5938ZM13.418 10.7812H4.39453C4.34791 10.7812 4.3032 10.7627 4.27024 10.7298C4.23727 10.6968 4.21875 10.6521 4.21875 10.6055V1.58203C4.21875 1.53541 4.23727 1.4907 4.27024 1.45774C4.3032 1.42477 4.34791 1.40625 4.39453 1.40625H13.418C13.4646 1.40625 13.5093 1.42477 13.5423 1.45774C13.5752 1.4907 13.5938 1.53541 13.5938 1.58203V10.6055C13.5938 10.6521 13.5752 10.6968 13.5423 10.7298C13.5093 10.7627 13.4646 10.7812 13.418 10.7812Z" fill="#BBBCC7"/>
            </svg>
          </motion.div>
          {
            (navigator && navigator.share) &&
            <button onClick={async() => {
              const shareData = {
                url: `https://www.goodgeorge.kiwi.nz/discount/${customer.referral}`,
                title: '$5 off your first purchase with Good George',
                text: '$5 off your first purchase with Good George',
              }
              try {
                await navigator.share(shareData)
              } catch(err) {
                console.log(err);
              }
            }} className={styles.button} style={{ backgroundColor: config.buttonColour ? config.buttonColour : undefined}}>
              <span>Share code</span>
            </button>
          }
        </div>
      }

      <div className={styles.links}>
        <Link to="/how" className={styles.link}>
          <span>How does it work?</span>
          <div>
            <svg aria-hidden="true" focusable="false" data-prefix="fas" data-icon="chevron-right" role="img" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 320 512"><path fill="currentColor" d="M285.476 272.971L91.132 467.314c-9.373 9.373-24.569 9.373-33.941 0l-22.667-22.667c-9.357-9.357-9.375-24.522-.04-33.901L188.505 256 34.484 101.255c-9.335-9.379-9.317-24.544.04-33.901l22.667-22.667c9.373-9.373 24.569-9.373 33.941 0L285.475 239.03c9.373 9.372 9.373 24.568.001 33.941z"></path></svg>
          </div>
        </Link>
        <Link to="/what" className={styles.link}>
          <span>What can I get?</span>
          <div>
            <svg aria-hidden="true" focusable="false" data-prefix="fas" data-icon="chevron-right" role="img" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 320 512"><path fill="currentColor" d="M285.476 272.971L91.132 467.314c-9.373 9.373-24.569 9.373-33.941 0l-22.667-22.667c-9.357-9.357-9.375-24.522-.04-33.901L188.505 256 34.484 101.255c-9.335-9.379-9.317-24.544.04-33.901l22.667-22.667c9.373-9.373 24.569-9.373 33.941 0L285.475 239.03c9.373 9.372 9.373 24.568.001 33.941z"></path></svg>
          </div>
        </Link>
        {
          /*
          Don't need settings right now, birthday is always set
          */

          /*
          <Link to="/settings" className={styles.link}>
            <span>Account settings</span>
            <div>
              <svg aria-hidden="true" focusable="false" data-prefix="fas" data-icon="chevron-right" role="img" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 320 512"><path fill="currentColor" d="M285.476 272.971L91.132 467.314c-9.373 9.373-24.569 9.373-33.941 0l-22.667-22.667c-9.357-9.357-9.375-24.522-.04-33.901L188.505 256 34.484 101.255c-9.335-9.379-9.317-24.544.04-33.901l22.667-22.667c9.373-9.373 24.569-9.373 33.941 0L285.475 239.03c9.373 9.372 9.373 24.568.001 33.941z"></path></svg>
            </div>
          </Link>
          */
        }
        <Link to="/activity" className={styles.link}>
          <span>Your activity</span>
          <div>
            <svg aria-hidden="true" focusable="false" data-prefix="fas" data-icon="chevron-right" role="img" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 320 512"><path fill="currentColor" d="M285.476 272.971L91.132 467.314c-9.373 9.373-24.569 9.373-33.941 0l-22.667-22.667c-9.357-9.357-9.375-24.522-.04-33.901L188.505 256 34.484 101.255c-9.335-9.379-9.317-24.544.04-33.901l22.667-22.667c9.373-9.373 24.569-9.373 33.941 0L285.475 239.03c9.373 9.372 9.373 24.568.001 33.941z"></path></svg>
          </div>
        </Link>
        <Link to="/terms" className={styles.link}>
          <span>Terms & conditions</span>
          <div>
            <svg aria-hidden="true" focusable="false" data-prefix="fas" data-icon="chevron-right" role="img" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 320 512"><path fill="currentColor" d="M285.476 272.971L91.132 467.314c-9.373 9.373-24.569 9.373-33.941 0l-22.667-22.667c-9.357-9.357-9.375-24.522-.04-33.901L188.505 256 34.484 101.255c-9.335-9.379-9.317-24.544.04-33.901l22.667-22.667c9.373-9.373 24.569-9.373 33.941 0L285.475 239.03c9.373 9.372 9.373 24.568.001 33.941z"></path></svg>
          </div>
        </Link>
      </div>
    </motion.div>
  )
}

export default Account;
